/**
 * BentoBox — flexible grid layout for bento-box-style cell configurations.
 *
 * Built on the Grid composite. Use {@link BentoBoxCell} as direct children (shortcodes, JSX).
 */

import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import { Grid, type GridProps } from '../Grid';
import {
  bentoCellVariants,
  type BentoCellVariantProps,
} from './bentobox.variants';

export interface BentoBoxProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of grid columns at full width (1–4). Layout collapses on smaller viewports. */
  columns?: 1 | 2 | 3 | 4;
  /** Gap between cells (same as Grid gap). Default: sm. */
  gap?: GridProps['gap'];
  /** Child cells; use {@link BentoBoxCell}. */
  children: React.ReactNode;
}

export interface BentoBoxCellProps
  extends React.HTMLAttributes<HTMLDivElement>,
    BentoCellVariantProps {
  colSpan?: 1 | 2 | 3 | 4;
  rowSpan?: 1 | 2 | 3;
  variant?: 'dark' | 'primary' | 'secondary' | 'none';
  padding?: 'default' | 'none';
  /**
   * `center` (default): flex column with items-center / justify-center for text stacks.
   * `start`: top-aligned, no flex wrapper (e.g. full-bleed media cells).
   */
  contentAlign?: 'start' | 'center';
  children: React.ReactNode;
}

export const BentoBoxCell = React.forwardRef<HTMLDivElement, BentoBoxCellProps>(
  (
    {
      className,
      colSpan = 1,
      rowSpan = 1,
      variant = 'dark',
      padding = 'default',
      contentAlign = 'center',
      children,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      role='group'
      className={cn(
        bentoCellVariants({
          variant,
          padding,
          colSpan,
          rowSpan,
          contentAlign,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);
BentoBoxCell.displayName = 'BentoBoxCell';

function columnsMdForBreakpoint(columns: 1 | 2 | 3 | 4): 2 | undefined {
  if (columns === 3 || columns === 4) {
    return 2;
  }
  return undefined;
}

export const BentoBox = React.forwardRef<HTMLDivElement, BentoBoxProps>(
  ({ className, columns = 3, gap = 'sm', children, ...props }, ref) => (
    <Grid
      ref={ref}
      className={cn(
        // Mobile: uniform row height for every direct grid child (cells and loose hen-ui-image).
        'w-full max-md:[&>*]:h-[288px] max-md:[&>*]:min-h-0 max-md:[&>*]:overflow-hidden',
        // Fill the grid item when Image is wrapped; direct-child imgs already get h-[288px] from [&>*].
        'max-md:[&>*_img]:h-full max-md:[&>*_img]:w-full max-md:[&>*_img]:min-h-0',
        className,
      )}
      columns={1}
      columns-md={columnsMdForBreakpoint(columns)}
      columns-lg={columns >= 2 ? columns : undefined}
      gap={gap}
      {...props}
    >
      {children}
    </Grid>
  ),
);
BentoBox.displayName = 'BentoBox';
